/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  ApiErrorDto,
  FlowItemDto,
  FlowRunDto,
  FlowRunSummaryDto,
  ListFlowRunItemsParams,
  ReviseFlowItemDto,
  StartFlowRunDto,
  WorkspaceFlowRunDto,
} from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType, BodyType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Run the flow's current draft (snapshotted as a version). Refused, with the
reasons, when the draft has errors or uses nodes that can't run yet.
 */
export const startFlowRun = (
  id: string,
  flowId: string,
  startFlowRunDto: BodyType<StartFlowRunDto>,
  signal?: AbortSignal,
) => {
  return customInstance<FlowRunDto>({
    url: `/api/v1/workspaces/${id}/flows/${flowId}/runs`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: startFlowRunDto,
    signal,
  });
};

export const getStartFlowRunMutationKey = () => ['startFlowRun'] as const;

export const getStartFlowRunMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof startFlowRun>>,
    TError,
    StartFlowRunMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof startFlowRun>>,
  TError,
  StartFlowRunMutationVariables,
  TContext
> => {
  const mutationKey = getStartFlowRunMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof startFlowRun>>,
    StartFlowRunMutationVariables
  > = (props) => {
    const { id, flowId, data } = props ?? {};

    return startFlowRun(id, flowId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type StartFlowRunMutationResult = NonNullable<
  Awaited<ReturnType<typeof startFlowRun>>
>;
export type StartFlowRunMutationBody = BodyType<StartFlowRunDto>;
export type StartFlowRunMutationError = ErrorType<ApiErrorDto>;
export type StartFlowRunMutationVariables = {
  id: string;
  flowId: string;
  data: BodyType<StartFlowRunDto>;
};

/**
 * @summary Run the flow's current draft (snapshotted as a version). Refused, with the
reasons, when the draft has errors or uses nodes that can't run yet.
 */
export const useStartFlowRun = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof startFlowRun>>,
      TError,
      StartFlowRunMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof startFlowRun>>,
  TError,
  StartFlowRunMutationVariables,
  TContext
> => {
  return useMutation(getStartFlowRunMutationOptions(options), queryClient);
};
/**
 * @summary The flow's runs, newest first.
 */
export const listFlowRuns = (id: string, flowId: string, signal?: AbortSignal) => {
  return customInstance<FlowRunSummaryDto[]>({
    url: `/api/v1/workspaces/${id}/flows/${flowId}/runs`,
    method: 'GET',
    signal,
  });
};

export const getListFlowRunsQueryKey = (id: string, flowId: string) => {
  return [`/api/v1/workspaces/${id}/flows/${flowId}/runs`] as const;
};

export const getListFlowRunsQueryOptions = <
  TData = Awaited<ReturnType<typeof listFlowRuns>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  flowId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listFlowRuns>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListFlowRunsQueryKey(id, flowId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listFlowRuns>>> = ({ signal }) =>
    listFlowRuns(id, flowId, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined && flowId !== null && flowId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listFlowRuns>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListFlowRunsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listFlowRuns>>
>;
export type ListFlowRunsQueryError = ErrorType<ApiErrorDto>;

export function useListFlowRuns<
  TData = Awaited<ReturnType<typeof listFlowRuns>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  flowId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listFlowRuns>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listFlowRuns>>,
          TError,
          Awaited<ReturnType<typeof listFlowRuns>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListFlowRuns<
  TData = Awaited<ReturnType<typeof listFlowRuns>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  flowId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listFlowRuns>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listFlowRuns>>,
          TError,
          Awaited<ReturnType<typeof listFlowRuns>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListFlowRuns<
  TData = Awaited<ReturnType<typeof listFlowRuns>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  flowId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listFlowRuns>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary The flow's runs, newest first.
 */

export function useListFlowRuns<
  TData = Awaited<ReturnType<typeof listFlowRuns>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  flowId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listFlowRuns>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListFlowRunsQueryOptions(id, flowId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Runs across the workspace: every open one, and those that finished in the
last day - newest first, with the steps they're on. Poll it while any is open.
 */
export const listWorkspaceFlowRuns = (id: string, signal?: AbortSignal) => {
  return customInstance<WorkspaceFlowRunDto[]>({
    url: `/api/v1/workspaces/${id}/flow-runs`,
    method: 'GET',
    signal,
  });
};

export const getListWorkspaceFlowRunsQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/flow-runs`] as const;
};

export const getListWorkspaceFlowRunsQueryOptions = <
  TData = Awaited<ReturnType<typeof listWorkspaceFlowRuns>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceFlowRuns>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListWorkspaceFlowRunsQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listWorkspaceFlowRuns>>> = ({
    signal,
  }) => listWorkspaceFlowRuns(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listWorkspaceFlowRuns>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListWorkspaceFlowRunsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listWorkspaceFlowRuns>>
>;
export type ListWorkspaceFlowRunsQueryError = ErrorType<ApiErrorDto>;

export function useListWorkspaceFlowRuns<
  TData = Awaited<ReturnType<typeof listWorkspaceFlowRuns>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceFlowRuns>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspaceFlowRuns>>,
          TError,
          Awaited<ReturnType<typeof listWorkspaceFlowRuns>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListWorkspaceFlowRuns<
  TData = Awaited<ReturnType<typeof listWorkspaceFlowRuns>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceFlowRuns>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspaceFlowRuns>>,
          TError,
          Awaited<ReturnType<typeof listWorkspaceFlowRuns>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListWorkspaceFlowRuns<
  TData = Awaited<ReturnType<typeof listWorkspaceFlowRuns>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceFlowRuns>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Runs across the workspace: every open one, and those that finished in the
last day - newest first, with the steps they're on. Poll it while any is open.
 */

export function useListWorkspaceFlowRuns<
  TData = Awaited<ReturnType<typeof listWorkspaceFlowRuns>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listWorkspaceFlowRuns>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListWorkspaceFlowRunsQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary A run's progress per node and edge, and its held items. Poll it while the run is open.
 */
export const getFlowRun = (id: string, runId: string, signal?: AbortSignal) => {
  return customInstance<FlowRunDto>({
    url: `/api/v1/workspaces/${id}/flow-runs/${runId}`,
    method: 'GET',
    signal,
  });
};

export const getGetFlowRunQueryKey = (id: string, runId: string) => {
  return [`/api/v1/workspaces/${id}/flow-runs/${runId}`] as const;
};

export const getGetFlowRunQueryOptions = <
  TData = Awaited<ReturnType<typeof getFlowRun>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  runId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getFlowRun>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetFlowRunQueryKey(id, runId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getFlowRun>>> = ({ signal }) =>
    getFlowRun(id, runId, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined && runId !== null && runId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getFlowRun>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetFlowRunQueryResult = NonNullable<Awaited<ReturnType<typeof getFlowRun>>>;
export type GetFlowRunQueryError = ErrorType<ApiErrorDto>;

export function useGetFlowRun<
  TData = Awaited<ReturnType<typeof getFlowRun>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  runId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getFlowRun>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFlowRun>>,
          TError,
          Awaited<ReturnType<typeof getFlowRun>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFlowRun<
  TData = Awaited<ReturnType<typeof getFlowRun>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  runId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getFlowRun>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFlowRun>>,
          TError,
          Awaited<ReturnType<typeof getFlowRun>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFlowRun<
  TData = Awaited<ReturnType<typeof getFlowRun>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  runId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getFlowRun>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary A run's progress per node and edge, and its held items. Poll it while the run is open.
 */

export function useGetFlowRun<
  TData = Awaited<ReturnType<typeof getFlowRun>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  runId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getFlowRun>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetFlowRunQueryOptions(id, runId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary What a run made, oldest first - all of it, or one node's.
 */
export const listFlowRunItems = (
  id: string,
  runId: string,
  params?: ListFlowRunItemsParams,
  signal?: AbortSignal,
) => {
  return customInstance<FlowItemDto[]>({
    url: `/api/v1/workspaces/${id}/flow-runs/${runId}/items`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListFlowRunItemsQueryKey = (
  id: string,
  runId: string,
  params?: ListFlowRunItemsParams,
) => {
  return [
    `/api/v1/workspaces/${id}/flow-runs/${runId}/items`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListFlowRunItemsQueryOptions = <
  TData = Awaited<ReturnType<typeof listFlowRunItems>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  runId: string,
  params?: ListFlowRunItemsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listFlowRunItems>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListFlowRunItemsQueryKey(id, runId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listFlowRunItems>>> = ({
    signal,
  }) => listFlowRunItems(id, runId, params, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined && runId !== null && runId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listFlowRunItems>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListFlowRunItemsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listFlowRunItems>>
>;
export type ListFlowRunItemsQueryError = ErrorType<ApiErrorDto>;

export function useListFlowRunItems<
  TData = Awaited<ReturnType<typeof listFlowRunItems>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  runId: string,
  params: undefined | ListFlowRunItemsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listFlowRunItems>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listFlowRunItems>>,
          TError,
          Awaited<ReturnType<typeof listFlowRunItems>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListFlowRunItems<
  TData = Awaited<ReturnType<typeof listFlowRunItems>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  runId: string,
  params?: ListFlowRunItemsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listFlowRunItems>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listFlowRunItems>>,
          TError,
          Awaited<ReturnType<typeof listFlowRunItems>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListFlowRunItems<
  TData = Awaited<ReturnType<typeof listFlowRunItems>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  runId: string,
  params?: ListFlowRunItemsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listFlowRunItems>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary What a run made, oldest first - all of it, or one node's.
 */

export function useListFlowRunItems<
  TData = Awaited<ReturnType<typeof listFlowRunItems>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  runId: string,
  params?: ListFlowRunItemsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listFlowRunItems>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListFlowRunItemsQueryOptions(id, runId, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Stop a run: queued work and reviews are cancelled; what it made stays.
 */
export const cancelFlowRun = (id: string, runId: string, signal?: AbortSignal) => {
  return customInstance<FlowRunDto>({
    url: `/api/v1/workspaces/${id}/flow-runs/${runId}/cancel`,
    method: 'POST',
    signal,
  });
};

export const getCancelFlowRunMutationKey = () => ['cancelFlowRun'] as const;

export const getCancelFlowRunMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof cancelFlowRun>>,
    TError,
    CancelFlowRunMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof cancelFlowRun>>,
  TError,
  CancelFlowRunMutationVariables,
  TContext
> => {
  const mutationKey = getCancelFlowRunMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof cancelFlowRun>>,
    CancelFlowRunMutationVariables
  > = (props) => {
    const { id, runId } = props ?? {};

    return cancelFlowRun(id, runId);
  };

  return { mutationFn, ...mutationOptions };
};

export type CancelFlowRunMutationResult = NonNullable<
  Awaited<ReturnType<typeof cancelFlowRun>>
>;

export type CancelFlowRunMutationError = ErrorType<ApiErrorDto>;
export type CancelFlowRunMutationVariables = { id: string; runId: string };

/**
 * @summary Stop a run: queued work and reviews are cancelled; what it made stays.
 */
export const useCancelFlowRun = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof cancelFlowRun>>,
      TError,
      CancelFlowRunMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof cancelFlowRun>>,
  TError,
  CancelFlowRunMutationVariables,
  TContext
> => {
  return useMutation(getCancelFlowRunMutationOptions(options), queryClient);
};
/**
 * @summary Rewrite a held item and check it again ("Rewrite line").
 */
export const reviseFlowItem = (
  id: string,
  itemId: string,
  reviseFlowItemDto: BodyType<ReviseFlowItemDto>,
  signal?: AbortSignal,
) => {
  return customInstance<FlowRunDto>({
    url: `/api/v1/workspaces/${id}/flow-items/${itemId}`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: reviseFlowItemDto,
    signal,
  });
};

export const getReviseFlowItemMutationKey = () => ['reviseFlowItem'] as const;

export const getReviseFlowItemMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof reviseFlowItem>>,
    TError,
    ReviseFlowItemMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof reviseFlowItem>>,
  TError,
  ReviseFlowItemMutationVariables,
  TContext
> => {
  const mutationKey = getReviseFlowItemMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof reviseFlowItem>>,
    ReviseFlowItemMutationVariables
  > = (props) => {
    const { id, itemId, data } = props ?? {};

    return reviseFlowItem(id, itemId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type ReviseFlowItemMutationResult = NonNullable<
  Awaited<ReturnType<typeof reviseFlowItem>>
>;
export type ReviseFlowItemMutationBody = BodyType<ReviseFlowItemDto>;
export type ReviseFlowItemMutationError = ErrorType<ApiErrorDto>;
export type ReviseFlowItemMutationVariables = {
  id: string;
  itemId: string;
  data: BodyType<ReviseFlowItemDto>;
};

/**
 * @summary Rewrite a held item and check it again ("Rewrite line").
 */
export const useReviseFlowItem = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof reviseFlowItem>>,
      TError,
      ReviseFlowItemMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof reviseFlowItem>>,
  TError,
  ReviseFlowItemMutationVariables,
  TContext
> => {
  return useMutation(getReviseFlowItemMutationOptions(options), queryClient);
};
